import { workoutTypeIcon } from '@client/new/pages/workout/components/workoutTypeIcon';
import { groupBy } from 'lodash';
import { PATHS } from '@client/routes';
import { toArray } from '@client/new/helpers/general';
import { Option } from '@client/new/helpers/types';
import {
  CellProps,
  CellSelectItem,
} from '@client/new/components/TableShared/Cell';
import {
  StandardTableProps,
  Row,
} from '@client/new/components/StandardTable/StandardTable';
import { SmartTableProps } from '@client/new/components/SmartTable/SmartTable';
import { StatusTagProps } from '@client/new/components/StatusTag/StatusTag';
import {
  DayName,
  LiteWorkout,
  WeekName,
  WorkoutsStructure,
} from '@client/new/pages/collection/workouts/workouts.domain';
import { workoutTypeToTab } from '@client/new/pages/collection/domain';
import {
  freeSampleCell,
  freeSampleColumn,
  getFormattedDate,
  statsCells,
  busyCells,
} from './workoutTableExtras';

// Direct action handlers for a published program row — no dispatch indirection.
export type ProgramWorkoutsActions = {
  onReorder: (workoutIds: number[]) => void;
  onDuplicate: (workoutId: number) => void;
  onArchive: (workoutId: number) => void;
  onCopyLink: (workoutId: number) => void;
  onConvertToRestDay: (workoutId: number) => void;
  onSetWeek: (p: {
    workoutId: number;
    newWeek: WeekName;
    day?: DayName;
  }) => void;
  onSetDay: (p: {
    workoutId: number;
    newDay: DayName;
    week?: WeekName;
  }) => void;
};

type BuildProgramWorkoutsTableParams = {
  influencerId: number;
  programName: string;
  workouts: LiteWorkout[];
  workoutsStructure: WorkoutsStructure;
  readonly: boolean;
  isProgramEditable: boolean;
  isWorkoutArchiveEnabled: boolean;
  actions: ProgramWorkoutsActions;
  busyWorkoutIds?: ReadonlySet<number>;
};

const fullTableSchema: StandardTableProps<number>['schema'] = [
  { width: 'stretchable', label: 'Workout name' },
  freeSampleColumn,
  { width: 100, label: 'rating' },
  { width: 100, label: 'begins' },
  { width: 100, label: 'completions' },
  { width: 100, label: 'comments' },
  { width: 50, label: 'actions', justify: 'right' },
];

const emptyWeekOption: CellSelectItem<WeekName> = {
  label: 'W',
  value: 'W' as WeekName,
  disabled: true,
};

const emptyDayOption: CellSelectItem<DayName> = {
  label: 'D',
  value: 'D' as DayName,
  disabled: true,
};

const weekOptions: Option<WeekName>[] = [
  emptyWeekOption,
  ...toArray(20, (i): Option<WeekName> => ({
    label: `W ${i + 1}`,
    value: `W${i + 1}` as WeekName,
  })),
];

const dayOptions: Option<DayName>[] = [
  emptyDayOption,
  ...toArray(7, (i): Option<DayName> => ({
    label: `D ${i + 1}`,
    value: `D${i + 1}` as DayName,
  })),
];

const archiveTooltip = (p: BuildProgramWorkoutsTableParams) =>
  p.isProgramEditable || p.isWorkoutArchiveEnabled
    ? 'Click here to store away workout for later reference, keeping your program organized'
    : 'Program is published and workouts can’t be archived. Please contact support if you need to proceed with this action';

// Selects are typed as `CellProps` (string) so they fit the row's cell array;
// the chosen value is branded back to WeekName/DayName inside onChange.
const weekDayCells = (
  p: BuildProgramWorkoutsTableParams,
  w: LiteWorkout,
): CellProps[] => [
  {
    type: 'Select',
    value: {
      items: weekOptions,
      toValue: (v) => v,
      fromValue: (v) => v,
      onChange: (v) =>
        p.actions.onSetWeek({
          workoutId: w.workoutId,
          newWeek: v as WeekName,
          day: w.day,
        }),
      value: w.week ?? emptyWeekOption.value,
    },
  },
  {
    type: 'Select',
    value: {
      items: dayOptions,
      toValue: (v) => v,
      fromValue: (v) => v,
      onChange: (v) =>
        p.actions.onSetDay({
          workoutId: w.workoutId,
          newDay: v as DayName,
          week: w.week,
        }),
      value: w.day ?? emptyDayOption.value,
    },
  },
];

const visibleTag: StatusTagProps = { type: 'ok', text: 'Visible' };

const publishedCells = (
  p: BuildProgramWorkoutsTableParams,
  w: LiteWorkout,
  tags?: StatusTagProps[],
): CellProps[] => {
  const inSeries = p.workoutsStructure === 'series';
  const title = inSeries ? w.orgTitle : w.fixedTitle;
  const subtitle = w.publishedDate
    ? 'Published ' + getFormattedDate(w.publishedDate)
    : '';

  const titleCell: CellProps = {
    type: 'Head',
    value: {
      showOrder: inSeries,
      title,
      subtitle,
      tags,
      titleIcon: workoutTypeIcon[w.workoutType],
      titleIconTestId: `table-cell-icon-${w.workoutType}`,
    },
  };

  const editURL = PATHS.WORKOUT_EDIT({
    workoutId: w.workoutId,
    influencerId: p.influencerId,
    programId: w.programId,
    fromWorkoutTab: workoutTypeToTab.published,
    workoutType: w.workoutType,
  });

  return [
    inSeries
      ? titleCell
      : { type: 'Items', value: [...weekDayCells(p, w), titleCell] },
    freeSampleCell(w),
    ...statsCells(w, p.influencerId, p.programName),
    {
      type: 'Menu',
      value: {
        items: [
          { text: p.readonly ? 'Preview' : 'Edit', to: editURL },
          {
            text: 'Copy link',
            onClick: () => p.actions.onCopyLink(w.workoutId),
          },
          {
            text: 'Duplicate',
            onClick: () => p.actions.onDuplicate(w.workoutId),
          },
          ...(w.workoutType !== 'rest' && !p.readonly
            ? [
                {
                  text: 'Convert to Rest Day',
                  onClick: () => p.actions.onConvertToRestDay(w.workoutId),
                },
              ]
            : []),
          {
            text: 'Archive',
            onClick: () => p.actions.onArchive(w.workoutId),
            disabled: !p.isWorkoutArchiveEnabled && !p.isProgramEditable,
            tooltip: archiveTooltip(p),
          },
        ],
      },
    },
  ];
};

const toRows = (
  p: BuildProgramWorkoutsTableParams,
  workouts: LiteWorkout[],
  visibleCount?: number,
): Array<Row<number>> =>
  workouts.map((w, i) => ({
    id: w.workoutId,
    cells: p.busyWorkoutIds?.has(w.workoutId)
      ? busyCells
      : publishedCells(
          p,
          w,
          visibleCount !== undefined && i < visibleCount
            ? [visibleTag]
            : undefined,
        ),
  }));

// Sentinel ids for the non-workout separator rows. Negative so they can never
// collide with a real (positive) workout id used as the React row key.
const DIVIDER_ROW_ID = -1;
const toWeekTitleRowId = (week: number) => -1000 - week;

const dividerRow: Row<number> = {
  id: DIVIDER_ROW_ID,
  single: { type: 'DividerRow', value: {} },
  cells: [],
};

const toWeekTitleRow = (week: number): Row<number> => ({
  id: toWeekTitleRowId(week),
  single: { type: 'TitleRow', value: 'WEEK ' + week },
  cells: [],
});

const toGroupedByWeekRows = (
  p: BuildProgramWorkoutsTableParams,
): Array<Row<number>> => {
  const byWeeks = groupBy(p.workouts, 'week');
  const notAssignedWeekName = undefined + '';
  const notAssignedWorkouts = byWeeks[notAssignedWeekName] || [];
  const sortedDays = (week: string) =>
    (byWeeks[week] || []).sort((l, r) => l.orgTitle.localeCompare(r.orgTitle));

  const rows = Object.keys(byWeeks)
    .sort((l, r) => l.localeCompare(r))
    .filter((w) => w !== notAssignedWeekName)
    .sort((l, r) => {
      const w1 = parseInt(l.replace(/^[Ww]/, ''), 10);
      const w2 = parseInt(r.replace(/^[Ww]/, ''), 10);
      return w1 === w2 ? 0 : w1 < w2 ? -1 : 1;
    })
    .reduce(
      (acc, w) => {
        const week = parseInt(w.replace(/^[Ww]/, ''), 10);
        return [...acc, toWeekTitleRow(week), ...toRows(p, sortedDays(w))];
      },
      [] as Array<Row<number>>,
    );

  return notAssignedWorkouts.length > 0
    ? [...rows, dividerRow, ...toRows(p, notAssignedWorkouts)]
    : rows;
};

// Builds the SmartTable props for a program's published workouts, including the
// week/day grouping + selects and (when eligible) draggable reordering. Every
// row action calls its handler directly — no VMDispatcher. `visibleCount` tags
// the first N rows as visible to members (limited categories).
export const buildProgramWorkoutsTable = (
  p: BuildProgramWorkoutsTableParams,
  { draggable, visibleCount }: { draggable: boolean; visibleCount?: number },
): SmartTableProps<number> => {
  const rows =
    p.workoutsStructure === 'week/day'
      ? toGroupedByWeekRows(p)
      : toRows(p, p.workouts, visibleCount);

  const value: StandardTableProps<number> = { schema: fullTableSchema, rows };

  return draggable
    ? {
        type: 'draggable',
        value: {
          ...value,
          onChange: (reordered) =>
            p.actions.onReorder(reordered.map((r) => r.id)),
        },
      }
    : { type: 'standard', value };
};
